import React from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';
import DataTable from 'react-data-table-component';
import { toastr } from 'react-redux-toastr';
import Layout from '../layout';

import Banner from '../k-wallet/banner';
import TransactionNoteTooltip from '../k-wallet/transaction-note-tooltip';
import TransactionDateTooltip from '../k-wallet/transaction-date-tooltip';

import KWalletActivateModal from '../shared/kwallet-activation-modal';
import TransferDialog from '../k-wallet/transfer-dialog';

const { useEffect, useState } = React;

const ManageKWallet = ({
    intl,
    activeWallet,
    accessToken,
    ...props
}) => {
    const parseDescription = (description, row) => description.replaceAll(/{%(\w+)%}/g, (match) => {
        const property = match.slice(2, match.length - 2);
        let value = row[property] !== undefined ? row[property] : '-';
        // created_on requires an extra parsing to format the date
        if (value !== '-' && property === 'created_on') {
            value = new Date(value).toLocaleDateString('en-US');
        }
        return value;
    });

    function getDetailedError(response) {
        let detailMessage = intl.formatMessage({ id: 'generic-error-title' });
        if (response && response.body && response.body.message) {
            switch (response.body.message) {
            case 'Account not found': detailMessage = intl.formatMessage({ id: 'transfer-funds-k-wallet-account-not-found' }); break;
            case 'Transfer between same account is not allowed': detailMessage = intl.formatMessage({ id: 'transfer-funds-k-wallet-same-account' }); break;
            default: detailMessage = intl.formatMessage({ id: 'generic-error-title' });
            }
        }
        return detailMessage;
    }

    const columns = [
        {
            name: intl.formatMessage({ id: 'transactions-table-date', defaultMessage: 'transactions-table-date' }),
            selector: (row) => row.created_on,
            wrap: true,
            cell: (row) => (
                <div>
                    <TransactionDateTooltip content={new Date(row.created_on).toLocaleDateString('en-US')} extra={new Date(row.created_on).toLocaleString()} />
                </div>
            ),
        },
        {
            name: intl.formatMessage({ id: 'transactions-table-description', defaultMessage: 'transactions-table-description' }),
            selector: (row) => row.description,
            cell: (row) => (
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        width: '100%',
                    }}
                >
                    <div>{parseDescription(row.description, row)}</div>
                    {row.notes && <TransactionNoteTooltip notes={row.notes} />}
                </div>
            ),
            wrap: true,
            grow: 3,
        },
        {
            name: intl.formatMessage({ id: 'transactions-table-status', defaultMessage: 'transactions-table-status' }),
            selector: (row) => row.status,
            format: (row) => (row.status ? row.status.toUpperCase() : ''),
        },
        {
            name: intl.formatMessage({ id: 'transactions-table-amount', defaultMessage: 'transactions-table-amount' }),
            selector: (row) => row.amount,
            format: (row, index) => `$ ${row.amount.toFixed(2)}`,
        },
    ];

    const [transactionsData, setTransactionsData] = useState([]);
    // eslint-disable-next-line no-undef
    const [walletDetails, setWalletDetails] = useState({});
    const [page, setPage] = useState(0);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [totalItems, setTotalItems] = useState(0);
    const [searchQuery, setSearchQuery] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [showActivateKWallet, setShowActivateKWallet] = useState(activeWallet);
    const [showTransferFundsKWallet, setShowTransferFundsKWallet] = useState(false);
    const [loadingTransferFundsKWallet, setLoadingTransferFundsKWallet] = useState(false);

    const customStyles = {
        headCells: {
            style: {
                fontSize: '16px',
                fontWeight: 'bold',
            },
        },
    };

    function fetchWalletDetails() {
        props.fetchWalletDetails(accessToken).then((response) => {
            setWalletDetails(response.body);
        });
    }

    function fetchWalletTransactions() {
        props
            .fetchWalletTransactions(accessToken, {
                page,
                size: itemsPerPage,
                q: searchQuery,
            })
            .then((response) => {
                const { content, page: pageResponse } = response.body;
                setTransactionsData(content);
                setTotalItems(pageResponse.total_elements);
                setIsLoading(false);
            });
    }

    useEffect(() => {
        if (!activeWallet && props.user.id) {
            setShowActivateKWallet(true);
        } else {
            setTimeout(fetchWalletDetails, 0);
        }

    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [accessToken]);

    useEffect(() => {
        setIsLoading(true);
        setTimeout(fetchWalletTransactions, 0);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, itemsPerPage, searchQuery, accessToken]);

    let ref = null;
    const onSearch = (q) => {
        if (ref) {
            clearTimeout(ref);
        }
        ref = setTimeout(() => {
            const param = new URLSearchParams();
            param.append('q', q);
            setSearchQuery(param.toString().slice(2));
        }, 500);
    };

    async function handleKWalletActivateModal(result) {
        if (result) {
            const activate = await props.activateWallet(accessToken);
            if (activate) {
                window.location.reload(); // cambiar a cargar el contenido correctamente.
            } else {
                window.location.assign('/');
            }
        } else if (props.user.id) {
            setShowActivateKWallet(false);
        } else {
            window.location.assign('/');
        }
    }

    function handleClickTransferFunds(e) {
        setShowTransferFundsKWallet(true);
    }

    function handleTransferFundsKWalletClose(e) {
        setShowTransferFundsKWallet(false);
    }

    function handleTransferFunds(values) {
        setLoadingTransferFundsKWallet(true);
        props.transferFunds(accessToken, values.amount, values.idTransfer).then((response) => {
            setShowTransferFundsKWallet(false);
            toastr.success(
                intl.formatMessage({ id: 'well', defaultMessage: 'Well done' }),
                intl.formatMessage({ id: 'transfer-funds-k-wallet-submitted-successfully', defaultMessage: 'Funds transferred successfully' }),
            );
            fetchWalletTransactions();
            fetchWalletDetails();
        }).catch(({ response }) => {
            setShowTransferFundsKWallet(false);
            toastr.error(
                intl.formatMessage({ id: 'error', defaultMessage: 'Error' }),
                getDetailedError(response),
            );
        }).finally(() => {
            setLoadingTransferFundsKWallet(false);
        });
    }

    return activeWallet ? (
        <Layout>
            <div className="body-container manager-k-wallet">
                <div className="container">
                    <div className="section-heading no-down-border">
                        <FormattedMessage id="manage-k-wallet" defaultMessage="manage-k-wallet" />
                    </div>
                    <Banner
                        accountBalance={walletDetails.balance}
                        availableBalance={walletDetails.available_balance}
                        activeWallet={activeWallet}
                        kWalletId={walletDetails.number}
                        transferFunds={handleClickTransferFunds}
                    />

                    <div className="section-heading no-down-border k-wallet-transactions">
                        <FormattedMessage id="manage-k-wallet-transactions" defaultMessage="Transacciones" />
                        <div>
                            <input
                                type="search"
                                className="form-control"
                                placeholder={intl.formatMessage({ id: 'transactions-table-search', defaultMessage: 'transactions-table-search' })}
                                onChange={(ev) => onSearch(ev.target.value)}
                            />
                        </div>
                    </div>
                    <DataTable
                        columns={columns}
                        data={transactionsData}
                        pagination
                        paginationServer
                        paginationTotalRows={totalItems}
                        onChangeRowsPerPage={(newPerPage) => setItemsPerPage(newPerPage)}
                        onChangePage={(page) => setPage(page - 1)}
                        customStyles={customStyles}
                        progressPending={isLoading}
                        noHeader
                        noDataComponent={(
                            <p style={{ padding: '20px', fontSize: '16px' }}>
                                {intl.formatMessage({ id: 'transactions-table-no-records', defaultMessage: 'transactions-table-no-records' })}
                            </p>
                        )}
                        progressComponent={(
                            <p style={{ padding: '20px', fontSize: '16px' }}>
                                {intl.formatMessage({ id: 'transactions-table-loading', defaultMessage: 'transactions-table-loading' })}
                            </p>
                        )}
                        paginationComponentOptions={{
                            rowsPerPageText: intl.formatMessage({ id: 'transactions-table-rows-per-page', defaultMessage: 'transactions-table-rows-per-page' }),
                        }}
                    />
                </div>
            </div>
            <TransferDialog
                isOpen={showTransferFundsKWallet}
                handleClose={handleTransferFundsKWalletClose}
                availableBalance={walletDetails.available_balance}
                transferFunds={handleTransferFunds}
                loading={loadingTransferFundsKWallet}
            />
        </Layout>
    ) : (
        <Layout>
            <div className="body-container manager-k-wallet">
                <div className="container">
                    <div className="section-heading no-down-border">
                        <FormattedMessage id="k-wallet" defaultMessage="K-Wallet" />
                    </div>
                    <Banner
                        accountBalance={walletDetails.balance}
                        availableBalance={walletDetails.available_balance}
                        kWalletId={walletDetails.number}
                        transferFunds={handleClickTransferFunds}
                    />
                </div>
            </div>
            <KWalletActivateModal
                isOpen={showActivateKWallet}
                handleClose={handleKWalletActivateModal}
            />
        </Layout>
    );
};

export default injectIntl(ManageKWallet);



/** WEBPACK FOOTER **
 ** ./src/components/pages/manage-k-wallet.jsx
 **/